<script lang="ts">
  import { theme } from '$lib/state/theme';

  type AppData = {
    id: string;
    name: string;
    description: string;
    keys: string[];
    hasIndexedDB?: boolean;
  };

  const appDataList: AppData[] = [
    {
      id: 'weather',
      name: 'Weather Notifications',
      description: 'Email, location, and weather preferences',
      keys: ['notify.center.v1']
    },
    {
      id: 'calculator',
      name: 'Calculator',
      description: 'Calculation history and settings',
      keys: ['calc.settings', 'calc.tape']
    },
    {
      id: 'rummy',
      name: 'Rummy Score Keeper',
      description: 'Current game state and scores',
      keys: ['rummy-score.current']
    },
    {
      id: 'terminaltrainer',
      name: 'Terminal Trainer',
      description: 'Mission progress, settings, weak skills, and local training state',
      keys: ['terminal-trainer.progress', 'terminal-trainer.settings']
    },
    {
      id: 'fillsign',
      name: 'Fill & Sign',
      description: 'Saved signature library',
      keys: ['signatureLibrary']
    },
    {
      id: 'checklist',
      name: 'Travel Checklist',
      description: 'Checklist items and completion status',
      keys: ['travel-checklist']
    },
    {
      id: 'mdviewer',
      name: 'GitHub Markdown Viewer',
      description: 'GitHub PAT token, cached files, and bookmarks',
      keys: ['md-viewer:pat', 'md-viewer:repos', 'md-viewer:bookmarks'],
      hasIndexedDB: true
    },
    {
      id: 'prefs',
      name: 'App Preferences',
      description: 'Pinned apps, categories, and layout preferences',
      keys: ['spa-studio.prefs.v1']
    }
  ];

  let selectedApps: Set<string> = new Set();
  let message = '';
  let isSuccess = false;

  function toggleApp(appId: string) {
    if (selectedApps.has(appId)) {
      selectedApps.delete(appId);
    } else {
      selectedApps.add(appId);
    }
    selectedApps = selectedApps;
  }

  async function clearSelected() {
    if (selectedApps.size === 0) {
      message = 'Please select at least one app to clear.';
      isSuccess = false;
      return;
    }

    const appNames = Array.from(selectedApps)
      .map(id => appDataList.find(a => a.id === id)?.name)
      .join(', ');

    const confirmed = window.confirm(
      `Are you sure you want to clear data for:\n\n${appNames}\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      for (const appId of selectedApps) {
        const app = appDataList.find(a => a.id === appId);
        if (!app) continue;

        // Clear localStorage keys
        for (const key of app.keys) {
          if (key.includes('*') || key.includes(':')) {
            const prefix = key.split(':')[0] + ':';
            const allKeys = Object.keys(localStorage);
            allKeys.forEach(k => {
              if (k.startsWith(prefix)) {
                localStorage.removeItem(k);
              }
            });
          } else {
            localStorage.removeItem(key);
          }
        }

        // Clear IndexedDB if applicable
        if (app.hasIndexedDB && app.id === 'mdviewer') {
          try {
            await indexedDB.deleteDatabase('md-viewer-db');
          } catch (e) {
            console.error('Failed to clear IndexedDB:', e);
          }
        }
      }

      message = `Successfully cleared data for ${selectedApps.size} app(s).`;
      isSuccess = true;
      selectedApps = new Set();

      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      message = 'Failed to clear data: ' + (error as Error).message;
      isSuccess = false;
    }
  }

  function clearAllData() {
    const confirmed = window.confirm(
      'Are you sure you want to clear ALL locally stored data?\n\n' +
      'This will delete all app preferences, saved data, and cached content.\n\n' +
      'This action cannot be undone.'
    );

    if (confirmed) {
      try {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
      } catch (error) {
        alert('Failed to clear data: ' + (error as Error).message);
      }
    }
  }

  // Clear message after 5 seconds
  $: if (message) {
    setTimeout(() => {
      message = '';
    }, 5000);
  }
</script>

<div class="settings-page">
  <div class="settings-hero">
    <h1>Settings</h1>
    <p class="settings-subtitle">Manage your preferences and data</p>
  </div>

  <!-- Theme Section -->
  <section class="settings-section">
    <div class="section-header-row">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--theme-primary))" stroke-width="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
      <h2>Appearance</h2>
    </div>
    <p class="section-desc">Choose your preferred color scheme. Your choice is saved across sessions.</p>

    <div class="theme-grid">
      <label class="theme-card" class:selected={$theme === 'light'}>
        <input type="radio" name="theme" value="light" checked={$theme === 'light'} on:change={() => theme.setTheme('light')} />
        <div class="theme-preview light-preview">
          <div class="preview-bar"></div>
          <div class="preview-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="theme-card-label">
          <span class="theme-card-name">Light</span>
          <span class="theme-card-desc">Bright and clean</span>
        </div>
      </label>

      <label class="theme-card" class:selected={$theme === 'dark'}>
        <input type="radio" name="theme" value="dark" checked={$theme === 'dark'} on:change={() => theme.setTheme('dark')} />
        <div class="theme-preview dark-preview">
          <div class="preview-bar"></div>
          <div class="preview-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="theme-card-label">
          <span class="theme-card-name">Dark</span>
          <span class="theme-card-desc">Easy on the eyes</span>
        </div>
      </label>
    </div>
  </section>

  <!-- Data Management -->
  <section class="settings-section">
    <div class="section-header-row">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--theme-primary))" stroke-width="1.5"><path d="M4 7V4h16v3M9 20h6M12 4v16"/></svg>
      <h2>Data Management</h2>
    </div>
    <p class="section-desc">Select which app data you want to clear. This permanently deletes the selected data from your browser.</p>

    <div class="app-list">
      {#each appDataList as app}
        <label class="app-item" class:checked={selectedApps.has(app.id)}>
          <div class="toggle-wrap">
            <input type="checkbox" checked={selectedApps.has(app.id)} on:change={() => toggleApp(app.id)} />
            <div class="toggle-track">
              <div class="toggle-thumb"></div>
            </div>
          </div>
          <div class="app-info">
            <div class="app-name">{app.name}</div>
            <div class="app-desc">{app.description}</div>
          </div>
        </label>
      {/each}
    </div>

    {#if message}
      <div class="message" class:success={isSuccess} class:error={!isSuccess}>
        {message}
      </div>
    {/if}

    <div class="actions">
      <button
        class="theme-btn theme-btn-primary theme-btn-sm"
        on:click={clearSelected}
        disabled={selectedApps.size === 0}
      >
        Clear Selected ({selectedApps.size})
      </button>
    </div>
  </section>

  <!-- Danger Zone -->
  <section class="settings-section danger-section">
    <div class="section-header-row">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--theme-destructive))" stroke-width="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01"/></svg>
      <h2>Danger Zone</h2>
    </div>
    <p class="section-desc">This will clear <strong>all</strong> data for <strong>all</strong> apps. This action cannot be undone.</p>
    <button class="btn-danger" on:click={clearAllData}>
      Clear All Data
    </button>
  </section>
</div>

<style>
  .settings-page {
    max-width: 700px;
    margin: 0 auto;
    padding: 1rem 1.5rem 4rem;
    color: hsl(var(--theme-foreground));
  }
  .settings-hero {
    margin-bottom: 2rem;
    padding-top: 1rem;
  }
  .settings-hero h1 {
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 0.25rem;
  }
  .settings-subtitle {
    font-size: 0.875rem;
    color: hsl(var(--theme-muted-foreground));
  }

  .settings-section {
    background: hsl(var(--theme-card));
    border: 1px solid hsl(var(--theme-border) / 0.4);
    border-radius: var(--theme-radius-xl);
    padding: 1.75rem;
    margin-bottom: 1.25rem;
  }
  .section-header-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.375rem;
  }
  .section-header-row h2 {
    font-size: 1.125rem;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  .section-desc {
    font-size: 0.8125rem;
    color: hsl(var(--theme-muted-foreground));
    line-height: 1.55;
    margin-bottom: 1.25rem;
  }
  .section-desc strong { color: hsl(var(--theme-foreground)); }

  /* Theme Cards */
  .theme-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.875rem;
  }
  .theme-card {
    position: relative;
    cursor: pointer;
    border-radius: var(--theme-radius-lg);
    border: 2px solid hsl(var(--theme-border));
    overflow: hidden;
    transition: all 250ms ease;
  }
  .theme-card input { position: absolute; opacity: 0; pointer-events: none; }
  .theme-card:hover { border-color: hsl(var(--theme-primary) / 0.4); }
  .theme-card.selected {
    border-color: hsl(var(--theme-primary));
    box-shadow: 0 0 0 3px hsl(var(--theme-primary) / 0.15);
  }
  .theme-preview {
    padding: 1rem;
    min-height: 70px;
  }
  .light-preview { background: #f8f9fa; }
  .dark-preview { background: #0f1320; }
  .preview-bar {
    height: 6px;
    width: 60%;
    border-radius: 3px;
    margin-bottom: 0.5rem;
  }
  .light-preview .preview-bar { background: #d1d5db; }
  .dark-preview .preview-bar { background: #2a3041; }
  .preview-dots {
    display: flex;
    gap: 0.375rem;
  }
  .preview-dots span {
    width: 20px;
    height: 14px;
    border-radius: 3px;
  }
  .light-preview .preview-dots span { background: #e5e7eb; }
  .dark-preview .preview-dots span { background: #1e2536; }
  .theme-card-label {
    padding: 0.75rem 1rem;
  }
  .theme-card-name {
    font-size: 0.8125rem;
    font-weight: 600;
    display: block;
    margin-bottom: 0.1rem;
  }
  .theme-card-desc {
    font-size: 0.6875rem;
    color: hsl(var(--theme-muted-foreground));
  }

  /* App List */
  .app-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
  }
  .app-item {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 0.875rem 1rem;
    border-radius: var(--theme-radius);
    border: 1px solid hsl(var(--theme-border) / 0.3);
    background: hsl(var(--theme-muted) / 0.05);
    cursor: pointer;
    transition: all 200ms ease;
  }
  .app-item:hover {
    background: hsl(var(--theme-muted) / 0.12);
    border-color: hsl(var(--theme-primary) / 0.2);
  }
  .app-item.checked {
    border-color: hsl(var(--theme-primary) / 0.3);
    background: hsl(var(--theme-primary) / 0.04);
  }

  /* Toggle Switch */
  .toggle-wrap {
    position: relative;
    flex-shrink: 0;
  }
  .toggle-wrap input {
    position: absolute;
    opacity: 0;
    width: 36px;
    height: 20px;
    cursor: pointer;
    z-index: 1;
  }
  .toggle-track {
    width: 36px;
    height: 20px;
    border-radius: 10px;
    background: hsl(var(--theme-muted) / 0.4);
    transition: background 200ms ease;
    position: relative;
  }
  .toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: hsl(var(--theme-muted-foreground));
    transition: all 200ms ease;
  }
  .toggle-wrap input:checked + .toggle-track {
    background: hsl(var(--theme-primary));
  }
  .toggle-wrap input:checked + .toggle-track .toggle-thumb {
    transform: translateX(16px);
    background: white;
  }

  .app-info { flex: 1; }
  .app-name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: hsl(var(--theme-foreground));
    margin-bottom: 0.125rem;
  }
  .app-desc {
    font-size: 0.75rem;
    color: hsl(var(--theme-muted-foreground));
  }

  .message {
    padding: 0.75rem 1rem;
    border-radius: var(--theme-radius-sm);
    margin-bottom: 1rem;
    font-size: 0.8125rem;
  }
  .message.success {
    background: hsl(var(--theme-success) / 0.1);
    border: 1px solid hsl(var(--theme-success) / 0.3);
    color: hsl(var(--theme-success));
  }
  .message.error {
    background: hsl(var(--theme-destructive) / 0.1);
    border: 1px solid hsl(var(--theme-destructive) / 0.3);
    color: hsl(var(--theme-destructive));
  }

  .actions { display: flex; gap: 0.75rem; }

  /* Danger Zone */
  .danger-section {
    border-color: hsl(var(--theme-destructive) / 0.2);
  }
  .danger-section h2 { color: hsl(var(--theme-destructive)); }
  .btn-danger {
    padding: 0.6rem 1.25rem;
    border-radius: var(--theme-radius);
    font-weight: 600;
    font-size: 0.8125rem;
    cursor: pointer;
    border: 1px solid hsl(var(--theme-destructive) / 0.3);
    background: hsl(var(--theme-destructive) / 0.08);
    color: hsl(var(--theme-destructive));
    transition: all 200ms ease;
    font-family: 'Inter', sans-serif;
  }
  .btn-danger:hover {
    background: hsl(var(--theme-destructive) / 0.15);
    border-color: hsl(var(--theme-destructive) / 0.5);
  }

  @media (max-width: 640px) {
    .settings-page { padding: 1rem 1rem 3rem; }
    .settings-section { padding: 1.25rem; }
    .theme-grid { grid-template-columns: 1fr; }
  }
</style>
